import { useCallback, useMemo } from 'react'
import { ArrayParam, StringParam, useQueryParams } from 'use-query-params'

import { FilterValues } from '../types/insights.types'
import {
  FilterAdapter,
  FilterFieldSpec,
  FilterStateController,
} from './filterAdapter.types'

// URL-query-param backing for filter values (the default). Filter state is then
// shareable and survives reload — the behaviour partner-dashboard relies on. The
// param config is derived from the field specs: list filters use `ArrayParam`,
// scalar filters use `StringParam`.

const buildConfig = (fields: FilterFieldSpec[]) =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.multiple ? ArrayParam : StringParam,
    ])
  )

export const urlFilterAdapter: FilterAdapter = {
  useFilterState: (fields: FilterFieldSpec[]): FilterStateController => {
    // Stable signature so the param config (and therefore the useQueryParams
    // subscription) is only rebuilt when the field set actually changes — filter
    // definitions load once and then hold steady.
    const signature = fields
      .map((field) => `${field.key}:${field.multiple}`)
      .join('|')
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const config = useMemo(() => buildConfig(fields), [signature])

    const [params, setParams] = useQueryParams(config)

    const setValue = useCallback(
      (key: string, value: string | string[] | undefined) =>
        setParams({ [key]: value }),
      [setParams]
    )

    const setValues = useCallback(
      (next: FilterValues) => setParams(next),
      [setParams]
    )

    const reset = useCallback(
      () =>
        setParams(
          Object.fromEntries(fields.map((field) => [field.key, undefined]))
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setParams, signature]
    )

    return { values: params as FilterValues, setValue, setValues, reset }
  },
}
